// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ValveConfiguratorState from "@store/Framework/Store/Store/Data/ValveConfigurator/ValveConfiguratorState";
import {
    ActuatorModel,
    ConfigureResponseModel,
    ElastomerModel,
    GateMaterialModel,
    OptionsResponseModel,
    ReadOnlyFieldsModel,
    SelectorsResponseModel,
} from "@store/Framework/Types/ValveConfiguratorApiModels";
import { Draft } from "immer";

const initialState: ValveConfiguratorState = {
    selectors: null,
    isLoadingSelectors: false,
    selectedSeries: null,
    selectedSize: null,
    selectedActuator: null,
    selectedGateMaterial: null,
    selectedElastomer: null,
    availableSizes: [],
    availableActuators: [],
    availableGateMaterials: [],
    availableElastomers: [],
    flangeDrillingLabel: "",
    flangeCode: "",
    isLoadingOptions: false,
    readOnlyFields: null,
    configurationResult: null,
    isSubmitting: false,
    hasSubmitted: false,
    error: null,
    mobileShowResults: false,
};

function sizesForSeries(selectors: SelectorsResponseModel | null, series: string | null): number[] {
    if (!selectors || !series) {
        return [];
    }

    const lookup = (key: string): number[] | undefined => {
        const sizes = selectors.sizes;
        if (!sizes) {
            return undefined;
        }
        if (Array.isArray(sizes[key])) {
            return sizes[key];
        }
        // Backend serializes Dictionary keys as camelCase, so "TG" arrives as "tg".
        // Fall back to a case-insensitive scan to remain robust to either casing.
        const target = key.toLowerCase();
        for (const k of Object.keys(sizes)) {
            if (k.toLowerCase() === target && Array.isArray(sizes[k])) {
                return sizes[k];
            }
        }
        return undefined;
    };

    const direct = lookup(series);
    if (direct) {
        return direct;
    }

    const unit = selectors.unitSystem?.trim();
    if (unit) {
        const composite = lookup(`${series}_${unit}`);
        if (composite) {
            return composite;
        }
    }

    return [];
}

const actions = {
    "Data/ValveConfigurator/BeginLoadSelectors": (draft: Draft<ValveConfiguratorState>) => {
        draft.isLoadingSelectors = true;
        draft.error = null;
    },
    "Data/ValveConfigurator/CompleteLoadSelectors": (
        draft: Draft<ValveConfiguratorState>,
        action: { result: SelectorsResponseModel },
    ) => {
        draft.selectors = action.result;
        draft.readOnlyFields = action.result.readOnlyFields ?? draft.readOnlyFields;
        draft.isLoadingSelectors = false;
    },
    "Data/ValveConfigurator/FailLoadSelectors": (draft: Draft<ValveConfiguratorState>, action: { message: string }) => {
        draft.isLoadingSelectors = false;
        draft.error = action.message;
    },

    "Data/ValveConfigurator/SetSeries": (draft: Draft<ValveConfiguratorState>, action: { series: string }) => {
        draft.selectedSeries = action.series;
        draft.availableSizes = sizesForSeries(draft.selectors, action.series);
        // Cascade reset: size, actuator, gate, elastomer, options, result
        draft.selectedSize = null;
        draft.selectedActuator = null;
        draft.selectedGateMaterial = null;
        draft.selectedElastomer = null;
        draft.availableActuators = [];
        draft.availableGateMaterials = [];
        draft.availableElastomers = [];
        draft.flangeDrillingLabel = "";
        draft.flangeCode = "";
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },
    "Data/ValveConfigurator/SetSize": (draft: Draft<ValveConfiguratorState>, action: { size: number | null }) => {
        draft.selectedSize = action.size;
        // Cascade reset: actuator, result (size change reloads options via thunk)
        draft.selectedActuator = null;
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },
    "Data/ValveConfigurator/SetActuator": (
        draft: Draft<ValveConfiguratorState>,
        action: { actuator: string | null },
    ) => {
        draft.selectedActuator = action.actuator;
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },
    "Data/ValveConfigurator/SetGateMaterial": (
        draft: Draft<ValveConfiguratorState>,
        action: { gateMaterial: string | null },
    ) => {
        draft.selectedGateMaterial = action.gateMaterial;
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },
    "Data/ValveConfigurator/SetElastomer": (
        draft: Draft<ValveConfiguratorState>,
        action: { elastomer: string | null },
    ) => {
        draft.selectedElastomer = action.elastomer;
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },

    "Data/ValveConfigurator/BeginLoadOptions": (draft: Draft<ValveConfiguratorState>) => {
        draft.isLoadingOptions = true;
        draft.error = null;
    },
    "Data/ValveConfigurator/CompleteLoadOptions": (
        draft: Draft<ValveConfiguratorState>,
        action: { result: OptionsResponseModel },
    ) => {
        draft.availableActuators = action.result.actuators ?? ([] as ActuatorModel[]);
        draft.availableGateMaterials = action.result.gateMaterials ?? ([] as GateMaterialModel[]);
        draft.availableElastomers = action.result.elastomers ?? ([] as ElastomerModel[]);
        draft.flangeDrillingLabel = action.result.flangeDrilling ?? "";
        draft.flangeCode = action.result.flangeCode ?? "";
        if (action.result.readOnlyFields) {
            draft.readOnlyFields = action.result.readOnlyFields as ReadOnlyFieldsModel;
        }
        draft.isLoadingOptions = false;
    },
    "Data/ValveConfigurator/FailLoadOptions": (draft: Draft<ValveConfiguratorState>, action: { message: string }) => {
        draft.isLoadingOptions = false;
        draft.error = action.message;
    },

    "Data/ValveConfigurator/BeginSubmitConfigure": (draft: Draft<ValveConfiguratorState>) => {
        draft.isSubmitting = true;
        draft.error = null;
    },
    "Data/ValveConfigurator/CompleteSubmitConfigure": (
        draft: Draft<ValveConfiguratorState>,
        action: { result: ConfigureResponseModel },
    ) => {
        draft.configurationResult = action.result;
        draft.hasSubmitted = true;
        draft.isSubmitting = false;
        // Auto-flip to results view on mobile after a successful submit.
        draft.mobileShowResults = true;
    },
    "Data/ValveConfigurator/FailSubmitConfigure": (
        draft: Draft<ValveConfiguratorState>,
        action: { message: string },
    ) => {
        draft.isSubmitting = false;
        draft.error = action.message;
    },

    "Data/ValveConfigurator/ResetResult": (draft: Draft<ValveConfiguratorState>) => {
        draft.configurationResult = null;
        draft.hasSubmitted = false;
    },

    "Data/ValveConfigurator/SetMobileShowResults": (
        draft: Draft<ValveConfiguratorState>,
        action: { show: boolean },
    ) => {
        draft.mobileShowResults = action.show;
    },
};

const reducer = createTypedReducerWithImmer(initialState, actions);
export type ValveConfiguratorDataActions = Parameters<typeof reducer>[1];
export default reducer;
